import { useEffect, useMemo, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import { useSWRConfig } from 'swr';

import { useAssortmentTest } from '~/pdp/context/AssortmentTestContext';
import getTailgateHiddenBehaviorTagId from '~/shared/utils/getTailgateHiddenBehaviorTagId';
import { useCart } from '~/techstyle-shared/react-cart';

import { getBuilderCollectionContent } from './getBuilderCollectionContent';
import { useProductGridDefaultParameters } from './useProductGridDefaultParameters';

export function useBuilderCollection({
  options,
  serverSideData,
  isTabGrid,
  automaticSizeFiltering,
  automaticGenderFiltering,
}) {
  const [clientCollectionContent, setClientCollectionContent] = useState();
  const productGridParameters = useProductGridDefaultParameters();
  productGridParameters.genderAutoFilterEnabled = automaticGenderFiltering;
  const { cache } = useSWRConfig();
  const { query } = useRouter();
  const apolloClient = useApolloClient();
  const cart = useCart({
    isLoginRequired: true,
  });
  const { excludeTagIds: baseExcludeTagIds, excludeFplCodes } =
    useAssortmentTest();

  const hiddenBehaviorTagId = getTailgateHiddenBehaviorTagId();
  const excludeTagIds = [
    ...(hiddenBehaviorTagId ? [hiddenBehaviorTagId] : []),
    ...(baseExcludeTagIds ?? []),
  ];

  // Memoize arrays to prevent unnecessary re-renders
  const excludeTagIdsString = JSON.stringify(excludeTagIds);
  const excludeFplCodesString = JSON.stringify(excludeFplCodes);

  const memoizedExcludeTagIds = useMemo(
    () => excludeTagIds,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeTagIdsString]
  );
  const memoizedExcludeFplCodes = useMemo(
    () => excludeFplCodes,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeFplCodesString]
  );

  useEffect(() => {
    if (!apolloClient) {
      return;
    }

    const builderCollectionContentParameters = {
      ...productGridParameters,
      options,
      query,
      isTabGrid,
      automaticSizeFiltering,
      cart,
      assortmentTestExcludeTagIds: memoizedExcludeTagIds,
      assortmentTestExcludeFplCodes: memoizedExcludeFplCodes,
    };
    const key = `useBuilderCollection:${JSON.stringify(
      builderCollectionContentParameters
    )}`;
    builderCollectionContentParameters.apolloClient = apolloClient;
    const getCollectionContent = async () => {
      // only run this if the server side data was not set
      if (serverSideData) {
        return null;
      }

      const collectionContent = await getBuilderCollectionContent(
        builderCollectionContentParameters
      );
      setClientCollectionContent(collectionContent);
      cache.set(key, collectionContent);
    };

    // this is used to leverage the product listing cache if we want to use caching
    const cachedCollectionContent = cache.get(key);
    if (cachedCollectionContent) {
      setClientCollectionContent(cachedCollectionContent);
    } else {
      getCollectionContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options,
    productGridParameters,
    serverSideData,
    apolloClient,
    memoizedExcludeTagIds,
    memoizedExcludeFplCodes,
  ]);

  return clientCollectionContent;
}
